import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, RotateCw, X } from 'lucide-react';
import { api } from '../lib/api-client';
import { Tooltip } from './Tooltip';

type NotificationItem = {
  kind: 'tender_ready' | 'rule_suggested';
  id: string;
  domain_id: string;
  domain_nom: string;
  summary: string;
  href: string;
  created_at: number;
};

type Notifs = {
  items: NotificationItem[];
  counts: { tenders_ready: number; rules_suggested: number };
};

const POLL_INTERVAL_MS = 60_000; // 1 min — assez frais sans flooder

export function NotificationsBell() {
  const [data, setData] = useState<Notifs | null>(null);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  function fetchNotifs() {
    api
      .get<Notifs>('/api/users/me/notifications')
      .then((d) =>
        setData({
          items: Array.isArray(d?.items) ? d.items : [],
          counts: {
            tenders_ready: d?.counts?.tenders_ready ?? 0,
            rules_suggested: d?.counts?.rules_suggested ?? 0,
          },
        }),
      )
      .catch(() => setData({ items: [], counts: { tenders_ready: 0, rules_suggested: 0 } }));
  }

  async function markRead(kind: string, refId: string) {
    try {
      await api.post('/api/users/me/notifications/read', { kind, refId });
      // Retire optimistement
      setData((d) =>
        d
          ? {
              ...d,
              items: d.items.filter((i) => !(i.kind === kind && i.id === refId)),
              counts: {
                tenders_ready:
                  (d.counts?.tenders_ready ?? 0) - (kind === 'tender_ready' ? 1 : 0),
                rules_suggested:
                  (d.counts?.rules_suggested ?? 0) - (kind === 'rule_suggested' ? 1 : 0),
              },
            }
          : d,
      );
    } catch (err) {
      // on réessaie un refresh
      fetchNotifs();
    }
  }

  async function markAllRead() {
    if (!data || data.items.length === 0) return;
    try {
      await api.post('/api/users/me/notifications/read-all', {
        items: data.items.map((i) => ({ kind: i.kind, refId: i.id })),
      });
      setData({ items: [], counts: { tenders_ready: 0, rules_suggested: 0 } });
    } catch (err) {
      fetchNotifs();
    }
  }

  useEffect(() => {
    fetchNotifs();
    const handle = window.setInterval(fetchNotifs, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, []);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const total = (data?.counts?.tenders_ready ?? 0) + (data?.counts?.rules_suggested ?? 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip
        label={`${total} notification${total !== 1 ? 's' : ''}`}
        side="bottom"
      >
        <button
          onClick={() => {
            setOpen(!open);
            if (!open) fetchNotifs();
          }}
          className="relative inline-flex items-center justify-center w-8 h-8 text-steel-300 hover:text-brand-400 hover:bg-ink-800 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
          aria-label={`${total} notification${total !== 1 ? 's' : ''}`}
          aria-expanded={open}
        >
          <Bell aria-hidden className="w-4 h-4" strokeWidth={1.75} />
          {total > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-brand-500 text-ink-950 text-2xs font-mono font-medium min-w-[16px] h-[16px] px-1 flex items-center justify-center">
              {total > 99 ? '99+' : total}
            </span>
          )}
        </button>
      </Tooltip>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 bg-ink-900 border border-ink-700 z-50 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 border-b border-ink-700 flex items-center justify-between">
            <h3 className="tc-label">Notifications</h3>
            <div className="flex items-center gap-3 text-2xs font-mono uppercase tracking-wider">
              {data && data.items.length > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-steel-500 hover:text-brand-400 transition-colors"
                >
                  mark all
                </button>
              )}
              <Tooltip label="Actualiser" side="bottom">
                <button
                  onClick={fetchNotifs}
                  aria-label="Actualiser"
                  className="inline-flex items-center justify-center w-6 h-6 text-steel-500 hover:text-brand-400 transition-colors"
                >
                  <RotateCw aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              </Tooltip>
            </div>
          </div>

          {!data || data.items.length === 0 ? (
            <p className="px-4 py-8 text-sm text-steel-500 text-center font-mono">
              no notifications
            </p>
          ) : (
            <ul>
              {data.items.map((item) => (
                <li key={`${item.kind}-${item.id}`} className="group border-b border-ink-700/50 last:border-b-0">
                  <div className="flex items-stretch">
                    <Link
                      to={item.href}
                      onClick={() => {
                        markRead(item.kind, item.id);
                        setOpen(false);
                      }}
                      className="flex-1 block px-4 py-3 hover:bg-ink-800 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <span className="font-mono text-2xs uppercase tracking-widest text-amber-500 mt-1 min-w-[2rem]">
                          {item.kind === 'tender_ready' ? 'RDY' : item.kind === 'rule_suggested' ? 'RUL' : 'MEN'}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-steel-100 truncate">{item.summary}</div>
                          <div className="text-2xs text-steel-500 mt-1 font-mono">
                            {item.domain_nom} · {new Date(item.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      </div>
                    </Link>
                    <Tooltip label="Marquer lu" side="left">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          markRead(item.kind, item.id);
                        }}
                        aria-label="Marquer lu"
                        className="px-3 text-steel-700 hover:text-steel-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
